import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Heart, ShoppingCart } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { StarRating } from '../components/StarRating';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [ratings, setRatings] = useState({});
  const [cardQty, setCardQty] = useState({});
  const [categories, setCategories] = useState(['All']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart, addToWishlist, isInWishlist } = useCart();

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) setSelectedCategory(categoryParam);
    axios.get(`${API}/categories`).then(r => setCategories(['All', ...r.data])).catch(() => {});
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
    fetchRatings();
  }, [selectedCategory, searchQuery]);

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/products/ratings`);
      setRatings(response.data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/products?`;
      if (selectedCategory && selectedCategory !== 'All') url += `category=${encodeURIComponent(selectedCategory)}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product, qty, e) => {
    e.stopPropagation();
    addToCart(product, qty);
    toast.success(`Added ${qty} item(s) to cart!`);
    setCardQty(prev => ({ ...prev, [product.id]: 1 }));
  };

  const handleToggleWishlist = (product, e) => {
    e.stopPropagation();
    addToWishlist(product);
    toast.success('Added to wishlist!');
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-12 py-8 md:py-12">
      <div className="mb-8">
        <h1 className="text-2xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}} data-testid="products-title">Our Products</h1>

        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-input" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category === 'All' ? '' : category)}
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${
                (category === 'All' && !selectedCategory) || selectedCategory === category
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-white border border-gray-300 text-[#0A192F] hover:border-[#D4AF37] hover:text-[#D4AF37]'
              }`}
              data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-[#D4AF37] border-t-transparent"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500" data-testid="no-products-message">No products found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="card card-hover overflow-hidden group relative cursor-pointer"
              onClick={() => navigate(`/products/${product.id}`)}
              data-testid={`product-card-${index}`}
            >
              {/* Wishlist */}
              <button
                onClick={(e) => handleToggleWishlist(product, e)}
                className="absolute top-2 right-2 z-10 bg-white/90 p-1.5 rounded-full shadow hover:scale-110 transition-transform"
                data-testid={`wishlist-btn-${product.id}`}
              >
                <Heart size={14} className={isInWishlist(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-500'} />
              </button>

              {/* Discount badge */}
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="absolute top-2 left-2 z-10 bg-[#D4AF37] text-white text-[10px] font-bold px-2 py-0.5 rounded-sm">
                  {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                </span>
              )}
              {/* Bestseller badge */}
              {product.bestseller && (
                <span className="absolute top-10 left-2 z-10 bg-[#0A192F] text-[#D4AF37] text-[9px] font-bold px-2 py-0.5 rounded-sm">
                  BESTSELLER
                </span>
              )}

              <div className="aspect-[3/4] overflow-hidden bg-gray-50">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>

              <div className="p-3">
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-1">{product.category}</p>
                <h3 className="font-bold text-sm mb-1 line-clamp-1">{product.name}</h3>
                {ratings[product.id] && (
                  <div className="flex items-center gap-1 mb-1" data-testid={`product-rating-${product.id}`}>
                    <StarRating rating={Math.round(ratings[product.id].averageRating)} size={12} />
                    <span className="text-[10px] text-gray-500">({ratings[product.id].totalReviews})</span>
                  </div>
                )}
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-[#D4AF37] font-bold text-base">₹{product.price}</span>
                  {product.originalPrice && product.originalPrice > product.price && <span className="text-gray-400 line-through text-xs">₹{product.originalPrice}</span>}
                </div>
                <div className="flex items-center gap-2 mb-2 text-[10px] text-gray-500">
                  {product.soldCount > 0 && <span data-testid={`sold-count-${product.id}`}>{product.soldCount} sold</span>}
                  <span className={product.stock > 0 ? 'text-green-600' : 'text-red-500'}>{product.stock > 0 ? `${product.stock} left` : 'Out of stock'}</span>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center border border-gray-300 rounded h-8">
                    <button onClick={(e) => { e.stopPropagation(); setCardQty(prev => ({...prev, [product.id]: Math.max(1, (prev[product.id] || 1) - 1)})); }} className="px-1.5 text-gray-500 hover:text-[#D4AF37] text-xs" data-testid={`qty-minus-${product.id}`}>-</button>
                    <span className="px-1.5 text-xs font-medium min-w-[20px] text-center">{cardQty[product.id] || 1}</span>
                    <button onClick={(e) => { e.stopPropagation(); setCardQty(prev => ({...prev, [product.id]: Math.min(product.stock, (prev[product.id] || 1) + 1)})); }} className="px-1.5 text-gray-500 hover:text-[#D4AF37] text-xs" data-testid={`qty-plus-${product.id}`}>+</button>
                  </div>
                  <Button
                    onClick={(e) => handleAddToCart(product, cardQty[product.id] || 1, e)}
                    className="flex-1 bg-[#D4AF37] text-white hover:bg-[#c9a22e] h-8 text-xs"
                    disabled={product.stock <= 0}
                    data-testid={`add-to-cart-btn-${product.id}`}
                  >
                    <ShoppingCart size={14} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
